import React from 'react';
import ReactDatePicker from 'react-datepicker';
import styled, { keyframes } from 'styled-components';
import { useIntl } from 'react-intl';
import { tokens } from '@companycam/slab-tokens';

import { languageWithoutRegionCode } from 'helpers/intl-helper';
import localePicker from 'helpers/intl-datepicker-helper';

export const CheckboxOptionsContainer = styled.div`
  max-height: 408px;
  overflow-y: auto;
  justify-content: space-between;
`;

export const CheckboxOption = styled.div`
  padding: 0.5rem;
`;

const calendarOpenAnimation = keyframes`
  0% {
    transform: translateY(-5%);
    opacity: 0;
  }
  100% {
    transform: translateY(0%);
    opacity: 1;
  }
`;

export const DatePickerWrapper = styled.div<{
  isSelected: boolean;
  isTodaySelected: boolean;
  'data-testid': string;
}>`
  // kind of hacky but without this override the pickers can render erroneously in modals (like view photo)
  z-index: 9;

  .react-datepicker__close-icon {
    &::after {
      line-height: 18px;
      height: 18px;
      width: 18px;
      color: white;
      right: 10px;
      top: 9px;
    }
  }

  // all the scss overrides
  .react-datepicker {
    border-radius: 8px;
    border: 1px solid ${tokens.color_border_default_le};
    box-shadow: 0 12px 12px ${tokens.color_border_input_inactive_le};
  }

  .react-datepicker__triangle {
    opacity: 0;
  }

  .react-datepicker__day--today {
    font-weight: 600;
    border-radius: 0.3rem;

    background-color: ${(props) =>
      props.isSelected ? 'white' : tokens.color_brand_secondary};

    color: ${(props) =>
      props.isSelected && !props.isTodaySelected
        ? tokens.color_brand_secondary
        : 'white'};
  }

  .react-datepicker__day {
    &:hover {
      background-color: ${tokens.color_button_background_subtle_le};
    }
  }

  .react-datepicker__day--selected {
    background-color: ${tokens.color_brand_secondary};
  }

  .react-datepicker__day--in-range {
    background-color: ${tokens.color_button_background_subtle_le};
    color: ${tokens.color_brand_secondary};
  }

  .react-datepicker__month-container {
    opacity: 1;
    animation-name: ${calendarOpenAnimation};
    animation-duration: 0.5s;
  }

  .react-datepicker__header {
    border-radius: 8px 8px 0 0;
    background-color: ${tokens.color_button_background_subtle_le};
    border-bottom: 1px solid ${tokens.color_background_3_le};
  }
`;

export const LocalizedDatePicker = (props: Record<string, unknown>) => {
  return (
    <ReactDatePicker
      locale={localePicker(languageWithoutRegionCode)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};

export const DatePicker = styled(LocalizedDatePicker)`
  height: 40px;
  width: ${(props) => {
    const { locale } = useIntl();
    if (props.selected) {
      return '126px';
    }
    return locale === 'es' ? '110px' : '100px';
  }};
  font-size: var(--cc_size_text_s);
  padding: 0px 12px !important;
  line-height: 40px;
  border-radius: var(--cc_size_border_radius_l);

  color: ${(props) =>
    props.selected ? 'white' : 'var(--cc_color_link_subtle)'} !important;

  background-color: ${(props) =>
    props.selected
      ? 'var(--cc_color_button_background_secondary)'
      : 'var(--cc_color_background_1)'};

  font-weight: 600;

  &::placeholder {
    color: var(--cc_color_link_subtle);
  }

  &:focus {
    border-color: var(--cc_color_link_secondary);
  }

  &:hover {
    border-color: var(--cc_color_link_secondary);
  }
`;

export default LocalizedDatePicker;
